import { MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react';
import {
  autoUpdate,
  flip,
  offset,
  safePolygon,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react';

import { Button, useToasts } from '@applyboard/crystal-ui';
import { copyToClipboard, useTranslation } from '@applyboard/ui-utils';
import { assert } from '~/utils/assert';

/**
 * Allows the user to quickly copy the child text or links
 * to the clipboard.
 * Currently does not support touch interfaces.
 *
 * @param children The content to copy. This can be any ReactNode.
 * If children contains any 'a' tags, the 'Copy link' button will be shown
 * for the a href attribute of the first a tag found.
 *
 * @param disabled If true children will be rendered directly without
 * copy buttons
 */
export function Copyable({ children, disabled }: { children?: ReactNode; disabled?: boolean }) {
  const { t } = useTranslation('Copyable');
  const [isOpen, setIsOpen] = useState(false);
  const copyRef = useRef<HTMLDivElement | null>(null);
  const toast = useToasts();
  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top-start',
    middleware: [offset(8), flip()],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const { linkHref } = useHrefAttributeObserver({ ref: copyRef });

  if (!children) {
    return null;
  }

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <>
      <div ref={refs.setReference} css={{ width: 'fit-content' }} {...getReferenceProps()}>
        <div ref={copyRef}>
          {isOpen ? (
            <mark css={theme => ({ background: theme.colors.bgPrimaryPressed, padding: 0 })}>
              {children}
            </mark>
          ) : (
            children
          )}
        </div>
      </div>

      <div
        ref={refs.setFloating}
        css={({ spacing }) => ({
          display: 'flex',
          gap: spacing[1],
          ...floatingStyles,
        })}
        {...getFloatingProps()}
      >
        <CopyableButton
          label={t('copy')}
          isOpen={isOpen}
          onBlur={() => setIsOpen(false)}
          onFocus={() => setIsOpen(true)}
          onClick={() => {
            copyToClipboard(copyRef.current?.innerText || '');
            toast.positive(t('copySuccess'));
          }}
        />
        {linkHref && (
          <CopyableButton
            label={t('copyLink')}
            isOpen={isOpen}
            onBlur={() => setIsOpen(false)}
            onFocus={() => setIsOpen(true)}
            onClick={() => {
              copyToClipboard(linkHref);
              toast.positive(t('copyLinkSuccess'));
            }}
          />
        )}
      </div>
    </>
  );
}

/**
 * This container should typically wrap the UI that renders the `Copyable` component.
 *
 * It's specifically needed in an edge-case where a grandparent of `Copyable`
 * has `pointer-events: auto` permanently set.  This is the case for `Drawer`
 *
 * See: https://applyboard.slack.com/archives/C06ME2XT59V/p1745953974354119?thread_ts=1745863354.440729&cid=C06ME2XT59V
 * See: https://floating-ui.com/docs/usehover#safepolygon
 */
export function CopyableContainer({ children }: { children: ReactNode }) {
  return (
    <div
      css={{
        '[data-floating-ui-safe-polygon] &': {
          pointerEvents: 'none',
        },
      }}
    >
      {children}
    </div>
  );
}

function CopyableButton({
  label,
  isOpen,
  onBlur,
  onClick,
  onFocus,
}: {
  label: string;
  isOpen: boolean;
  onBlur: () => void;
  onClick: () => void;
  onFocus: () => void;
}) {
  return (
    <div
      css={theme =>
        isOpen
          ? {
              borderRadius: theme.spacing[2],
              boxShadow:
                '0px 4px 10px 0px rgba(59, 65, 83, 0.12), 0px 1px 6px 0px rgba(59, 65, 83, 0.14), 0px 2px 9px 0px rgba(59, 65, 83, 0.19)',
              opacity: 1,
            }
          : {
              clip: 'rect(0 0 0 0)',
              opacity: 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              width: '1px',
            }
      }
    >
      <Button
        onBlur={onBlur}
        onClick={onClick}
        onFocus={onFocus}
        emphasis="outlined"
        intent="primary"
        size="sm"
      >
        {label}
      </Button>
    </div>
  );
}

function useHrefAttributeObserver({ ref }: { ref: MutableRefObject<HTMLDivElement | null> }) {
  const [linkHref, setLinkHref] = useState<string | null>(null);

  // eslint-disable-next-line no-restricted-syntax -- Need to query the DOM directly
  useEffect(() => {
    const queryChildHref = () => {
      if (ref.current) {
        const anchors = ref.current.querySelectorAll('a');

        if (anchors.length > 0) {
          const href = anchors[0].getAttribute('href');
          assert(href, 'href not defined for link');
          setLinkHref(href?.startsWith('http') ? href : `${window.location.origin}${href}`);
        }
      }
    };
    if (ref.current) {
      queryChildHref();
      const observer = new MutationObserver(queryChildHref);
      observer.observe(ref.current, { childList: true, subtree: true, attributes: true });
      return () => observer.disconnect();
    }
  }, [ref]);

  return {
    linkHref,
  };
}
